import { useState, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogProps,
  JFormik,
  selectUser,
} from '@centerpiece/components';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip,
  CircularProgress,
  Grid,
  Theme,
} from '@mui/material';
import { ArrowBack, Save, Edit, Unsubscribe } from '@mui/icons-material';
import * as Yup from 'yup';
import moment from 'moment';
import {
  Action,
  Coordination,
  Attachment,
  Contact,
  Message,
  Response,
  Event,
  Reaction,
  MessageContact,
} from 'store/api/models';
import {
  useCreateMessageMutation,
  useLazyGetAttachmentQuery,
  usePatchMessageMutation,
} from 'store/api';
import {
  CreateMessageStatusProps,
  CreateMessageStatus,
} from 'statuses/CreateMessageStatus';
import { downloadFile } from 'utils/downloadFile.util';
import { useMessageForm } from 'jformik/useMessageForm';
import { useSelector } from 'react-redux';
import { ContactComponent } from 'components/ContactComponent';
import { useItemContext } from 'contexts/item.context';
import { useAllContacts } from 'utils/contacts.utils';

export interface MessageTabProps {
  onClose?: Function;
  onSubmit?: (values: any) => void | Promise<any>;
  // coordination?: Coordination;
  // event?: Event;
  message?: Message;
  response?: Response;
  back?: boolean;
}

export const MessageTab = ({
  onClose = () => {},
  onSubmit = values => {},
  // coordination,
  // event,
  message,
  response,
  back = false,
}: MessageTabProps) => {
  // eslint-disable-next-line
  const user = useSelector(selectUser);
  const {
    id,
    project,
    project_id,
    item,
    title,
    itemLoading,
    // coordination,
    task,
    // event,
    refetchItem,
    setActiveTab,
    messages,
  } = useItemContext();
  const replied =
    messages?.find(m => m.message.id === message?.id)?.responses?.length ||
    0 > 0;
  const allContacts = useAllContacts();
  const formRef = useRef<any>(null);
  const [createMessage] = useCreateMessageMutation();
  const [patchMessage, patchMessageResults] = usePatchMessageMutation();
  const [getAttachment] = useLazyGetAttachmentQuery({ refetchOnFocus: false });
  const [messageStatus, setMessageStatus] = useState<
    Omit<CreateMessageStatusProps, 'close'>
  >({
    open: false,
    loading: false,
    creatingMessage: false,
    creatingMessageError: false,
    attachments: { pending: 0, created: 0, failed: 0 },
  });
  const [alertDetails, setAlertDetails] = useState<AlertDialogProps>();
  const [editing, setEditing] = useState<boolean | undefined>(
    message === undefined ? undefined : false
  );
  const [preview, setPreview] = useState<boolean>(false);
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    close?: Function;
    initialValues?: any;
  }>({ open: false });
  const messageForm: any[] = useMessageForm({
    preview: preview,
    disabled: editing === false, // !!message || !!response,
    editing: editing,
    replied: !!response,
    setOpenContact: setOpenContact,
    contacts: allContacts,
    onDownload: async (file: any) => {
      const attachment: Attachment = await getAttachment(file.id).unwrap();
      await downloadFile(attachment);
    },
  });

  const handleToggle = () => {
    if (formRef.current) {
      const { values, setFieldValue } = formRef.current;
      const cleanvalues = values?.form?.body?.map((item: any) => {
        if (item?.action) {
          const cleanItem = {
            ...item,
            action: {
              ...item.action,
              reactiondetails: undefined,
              reactionattachments: undefined,
              status: undefined,
            },
          };
          return {
            ...cleanItem,
            form: {
              ...cleanItem,
            },
          };
        } else if (item?.preamble) {
          return { ...item, preamble: undefined, replyattachments: undefined };
        } else {
          return item;
        }
      });
      setFieldValue('form.body', cleanvalues);
      setPreview(prev => !prev);
    }
  };
  return (
    <Box>
      <AlertDialog
        open={!!alertDetails?.open}
        title={alertDetails?.title || ''}
        content={alertDetails?.content || ''}
        sx={alertDetails?.sx || {}}
        PaperProps={alertDetails?.PaperProps || {}}
        onClose={() => {
          alertDetails?.onClose && alertDetails.onClose();
          setAlertDetails(undefined);
        }}
        onConfirm={() => {
          alertDetails?.onConfirm && alertDetails.onConfirm();
          setAlertDetails(undefined);
        }}
      />
      {openContact?.open && (
        <ContactComponent
          enableOrganization={true}
          {...openContact}
          close={() => {
            openContact.close && openContact.close();
            setOpenContact({ open: false });
          }}
          fromMessage
        />
      )}
      <Box
        sx={{
          display: openContact?.open ? 'none' : 'block',
          position: 'relative',
        }}
      >
        <CreateMessageStatus
          close={() =>
            setMessageStatus({
              open: false,
              loading: false,
              creatingMessage: false,
              creatingMessageError: false,
              attachments: { pending: 0, created: 0, failed: 0 },
            })
          }
          {...messageStatus}
        />
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap={'nowrap'}
          spacing={0}
          sx={{ width: '100%' }}
        >
          <Grid item>
            {back && (
              <Button
                startIcon={<ArrowBack />}
                color="secondary"
                onClick={() => onClose()}
              >
                Back
              </Button>
            )}
          </Grid>

          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              flexWrap={'nowrap'}
              spacing={0}
            >
              {!response && (
                <Grid item sx={{ pb: 1 }}>
                  <Tooltip
                    title={
                      'Toggle on to see what your recipients will see. Any edits made in this mode will be discarded when the message is sent'
                    }
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            color="secondary"
                            checked={preview}
                            onChange={() => handleToggle()}
                          />
                        }
                        label={
                          <Typography variant="button">
                            EMAIL PREVIEW
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Tooltip>
                </Grid>
              )}
              {editing && !replied && (
                <Grid item>
                  <Button
                    startIcon={
                      patchMessageResults.isLoading ? (
                        <CircularProgress sx={{ fontSize: '0.5rem' }} />
                      ) : (
                        <Unsubscribe fontSize="small" />
                      )
                    }
                    color="warning"
                    // onClick={() => setEditing(prev => !prev)}
                    onClick={async () => {
                      if (editing) {
                        setAlertDetails({
                          open: true,
                          PaperProps: {
                            sx: {
                              border: (theme: Theme) =>
                                `10px solid ${theme.palette.warning.light}`,
                            },
                          },
                          title: `Are you sure you want to cancel this message?`,
                          content: `The email will be recalled from all recipients.`,
                          onConfirm: async () => {
                            setEditing(false);
                            if (formRef.current) {
                              const { submitForm, setFieldValue } =
                                formRef.current;
                              setFieldValue('recipients', []);
                              setFieldValue('cc', []);
                              setFieldValue('bcc', []);
                              setFieldValue('form.canceled', true);
                              submitForm();
                            }
                          },
                          onClose: () => setEditing(false),
                        });
                      } else {
                        setEditing(true);
                      }
                    }}
                  >
                    Cancel Message
                  </Button>
                </Grid>
              )}
              {message && !replied && (
                <Grid item>
                  <Button
                    startIcon={
                      patchMessageResults.isLoading ? (
                        <CircularProgress sx={{ fontSize: '0.5rem' }} />
                      ) : editing ? (
                        <Save fontSize="small" />
                      ) : (
                        <Edit fontSize="small" />
                      )
                    }
                    color="secondary"
                    // onClick={() => setEditing(prev => !prev)}
                    onClick={async () => {
                      if (editing) {
                        setAlertDetails({
                          open: true,
                          title: `Are you sure you want to update this message?`,
                          content: `The email link will be updated and those who read the prior version will receive a new email indicating there has been an update.`,
                          onConfirm: async () => {
                            setEditing(false);
                            if (formRef.current) {
                              formRef.current.submitForm();
                            }
                          },
                          onClose: () => setEditing(false),
                        });
                      } else {
                        setEditing(true);
                      }
                    }}
                  >
                    {editing ? 'Save' : 'Edit'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ paddingTop: back ? 1 : 0 }}>
          <Box>
            <JFormik
              key={JSON.stringify(formRef.current?.values?.recipients)}
              formRef={formRef}
              initialValues={
                formRef.current?.values
                  ? { ...formRef.current.values }
                  : !!response
                  ? {
                      ...response.r_message_r,
                      recipients: response?.contacts
                        ?.filter(
                          (contact: Contact) =>
                            !contact.author && contact.message_type === 'TO'
                        )
                        .map((contact: Contact) => ({
                          ...contact,
                          name:
                            contact.firstname && contact.firstname !== ''
                              ? `${contact.firstname} ${contact.lastname}`
                              : contact.email,
                        })),
                      cc: response?.contacts
                        ?.filter(
                          (contact: Contact) =>
                            !contact.author && contact.message_type === 'CC'
                        )
                        .map((contact: Contact) => ({
                          ...contact,
                          name:
                            contact.firstname && contact.firstname !== ''
                              ? `${contact.firstname} ${contact.lastname}`
                              : contact.email,
                        })),
                      bcc: response?.contacts
                        ?.filter(
                          (contact: Contact) =>
                            !contact.author && contact.message_type === 'BCC'
                        )
                        .map((contact: Contact) => ({
                          ...contact,
                          name:
                            contact.firstname && contact.firstname !== ''
                              ? `${contact.firstname} ${contact.lastname}`
                              : contact.email,
                        })),
                      // type: coordination?.type || event?.type,
                      from: [
                        {
                          ...response.contact_r,
                          name: `${response.contact_r.firstname} ${response.contact_r.lastname}`,
                        },
                      ],
                      form: {
                        ...response.form,
                        body: response.form?.body?.map(
                          (item: any, index: number) => {
                            if (item.type) {
                              const reaction = response.reactions?.find(
                                (a: any) =>
                                  a.form?.name === `form.body.${index}.action`
                              );
                              if (reaction) {
                                return {
                                  ...item,
                                  action: {
                                    ...item.action,
                                    attachments:
                                      reaction?.action_r?.attachments,
                                    reactionattachments:
                                      reaction?.reactionattachments,
                                  },
                                };
                              } else {
                                return item;
                              }
                            } else if (item.preamble) {
                              return {
                                ...item,
                                replyattachments: response.attachments,
                              };
                            } else {
                              return item;
                            }
                          }
                        ) || [
                          {
                            text: `${
                              response.r_message_r.details
                                ? `${response.r_message_r.details}\n\n`
                                : ''
                            }${
                              response?.reactions?.[0]?.action_r?.title
                                ? `${response?.reactions?.[0]?.action_r?.title}:\n`
                                : ''
                            }${
                              response?.reactions?.[0]?.action_r?.details
                                ? response?.reactions?.[0]?.action_r?.details
                                : ''
                            }`,
                          },
                          ...(response?.reactions?.map(
                            (reaction: Reaction, index: number) => {
                              let item = {
                                // type:
                                //   coordination?.type === 'Approval'
                                //     ? 'approval_request'
                                //     : coordination?.type === 'Readiness'
                                //     ? 'readiness_request'
                                //     : 'share_file',
                                action: {
                                  ...reaction?.action_r,
                                  status: reaction.status.replaceAll('-', ' '),
                                  reactiondetails: reaction.details,
                                  reactionattachments:
                                    reaction.reactionattachments,
                                },
                                text: '',
                              };

                              if (response.reactions.length - 1 >= index + 1) {
                                item.text = `${
                                  reaction?.action_r?.details
                                    ? `${reaction?.action_r?.details}\n\n`
                                    : ''
                                }${
                                  response?.reactions?.[index + 1]?.action_r
                                    ?.title
                                    ? `${
                                        response?.reactions?.[index + 1]
                                          ?.action_r?.title
                                      }:\n`
                                    : ''
                                }${
                                  response?.reactions?.[index + 1]?.action_r
                                    ?.details
                                    ? response?.reactions?.[index + 1]?.action_r
                                        ?.details
                                    : ''
                                }`;
                              }
                              return item;
                            }
                          ) || []),
                        ],
                      },
                    }
                  : message
                  ? {
                      ...message,
                      recipients: message?.contacts
                        ?.filter(
                          (contact: Contact) =>
                            !contact.author && contact.message_type === 'TO'
                        )
                        .map((contact: Contact) => ({
                          ...contact,
                          name:
                            contact.firstname && contact.firstname !== ''
                              ? `${contact.firstname} ${contact.lastname}`
                              : contact.email,
                        })),
                      cc: message?.contacts
                        ?.filter(
                          (contact: Contact) =>
                            !contact.author && contact.message_type === 'CC'
                        )
                        .map((contact: Contact) => ({
                          ...contact,
                          name:
                            contact.firstname && contact.firstname !== ''
                              ? `${contact.firstname} ${contact.lastname}`
                              : contact.email,
                        })),
                      bcc: message?.contacts
                        ?.filter(
                          (contact: Contact) =>
                            !contact.author && contact.message_type === 'BCC'
                        )
                        .map((contact: Contact) => ({
                          ...contact,
                          name:
                            contact.firstname && contact.firstname !== ''
                              ? `${contact.firstname} ${contact.lastname}`
                              : contact.email,
                        })),
                      // type: coordination?.type || event?.type,
                      from: [
                        {
                          ...message.contact_r,
                          name: `${message.contact_r.firstname} ${message.contact_r.lastname}`,
                        },
                      ],
                      form: {
                        ...message.form,
                        canceled: false,
                        body: message.form?.body?.map((item: any) => {
                          if (item.type && item.form?.name) {
                            const action = message.actions?.find(
                              (a: any) => a.form?.name === item.form?.name
                            );
                            if (action) {
                              return {
                                ...item,
                                action: {
                                  ...action,
                                  ...item.action,
                                  id: action.id,
                                  attachments: action?.attachments,
                                },
                              };
                            } else {
                              return item;
                            }
                          } else {
                            return item;
                          }
                        }) || [
                          {
                            text: `${
                              message.details ? `${message.details}\n\n` : ''
                            }${
                              message?.actions?.[0]?.title
                                ? `${message?.actions?.[0]?.title}:\n`
                                : ''
                            }${
                              message?.actions?.[0]?.details
                                ? message?.actions?.[0]?.details
                                : ''
                            }`,
                          },
                          ...(message?.actions?.map(
                            (action: Action, index: number) => {
                              let item = {
                                // type:
                                //   coordination?.type === 'Approval'
                                //     ? 'approval_request'
                                //     : coordination?.type === 'Readiness'
                                //     ? 'readiness_request'
                                //     : 'share_file',
                                action: message?.actions?.[index],
                                text: '',
                              };

                              if (message.actions.length - 1 >= index + 1) {
                                item.text = `${
                                  message.details
                                    ? `${message.details}\n\n`
                                    : ''
                                }${
                                  message?.actions?.[index + 1]?.title
                                    ? `${
                                        message?.actions?.[index + 1]?.title
                                      }:\n`
                                    : ''
                                }${
                                  message?.actions?.[index + 1]?.details
                                    ? message?.actions?.[index + 1]?.details
                                    : ''
                                }`;
                              }
                              return item;
                            }
                          ) || []),
                        ],
                      },
                    }
                  : {
                      manual: true,
                      from: [
                        {
                          ...user,
                          name: `${user.firstname} ${user.lastname}`,
                        },
                      ],
                      subject: `${project?.title}: ${title}`,
                      type: 'Default',
                      form: { body: [{ text: '' }], canceled: false },
                      // form: {
                      //   body: [
                      //     {
                      //       type: 'auto_salutation',
                      //       text: 'Hey {{first_name}},\n',
                      //       disabled: true,
                      //     },
                      //   ],
                      //   auto_salutation: true,
                      // },
                    }
              }
              disabled={editing === false} //!!message || messageStatus.open}
              validationSchema={Yup.object()
                .shape({
                  subject: Yup.string()
                    .nullable()
                    .required(`Please add a subject.`),
                  form: Yup.object().shape({
                    body: Yup.array()
                      .nullable()
                      .min(1, `Please add an email message.`)
                      .required('Please add an email message.'),
                  }),
                })
                .test(
                  'recipients',
                  'At least one field must have a value',
                  function (this: Yup.TestContext, value) {
                    const { path, createError } = this;
                    const { recipients = [], cc = [], bcc = [] } = value;
                    if (
                      !(
                        recipients?.length > 0 ||
                        cc?.length > 0 ||
                        bcc?.length > 0
                      )
                    ) {
                      const keys = ['recipients', 'cc', 'bcc'];
                      const errors = keys
                        .map(key => {
                          return new Yup.ValidationError(
                            'A valid email address is required in To, Cc, or Bcc',
                            value[key],
                            key
                          );
                        })
                        .filter(Boolean);
                      return createError({
                        message: () => errors,
                      });
                    }
                    return false;
                  }
                )}
              handleSubmit={async (values: any) => {
                delete values['listsubject'];
                delete values['tooltip'];
                delete values['created_at'];
                const numFiles: number =
                  values?.form?.body?.reduce(
                    (total: number, item: any) =>
                      total +
                        item?.action?.attachments?.filter((a: any) => !a.id)
                          ?.length || 0,
                    0
                  ) || 0;
                setMessageStatus({
                  open: true,
                  loading: true,
                  creatingMessage: true,
                  creatingMessageError: false,
                  attachments: { pending: numFiles, created: 0, failed: 0 },
                  edit: !!values.id,
                });
                const msgvalues = {
                  ...values,
                  form: {
                    ...values.form,
                    body: values?.form?.body
                      ?.map((item: any, index: number) => {
                        if (item.type) {
                          return {
                            ...item,
                            form: {
                              ...item,
                              name: `form.body.${index}.action`,
                            },
                          };
                        } else {
                          return item;
                        }
                      })
                      ?.filter((i: any) => i),
                  },
                  actions: values?.form?.body
                    ?.map((item: any, index: number) => {
                      if (item.type) {
                        return {
                          ...item?.action,
                          form: {
                            ...item,
                            name: `form.body.${index}.action`,
                          },
                        };
                      }
                    })
                    ?.filter((i: any) => i),
                  item: id,
                  type: 'Default',
                };
                try {
                  let newMessage: Message;
                  if (msgvalues.id > 0) {
                    newMessage = await patchMessage(msgvalues).unwrap();
                  } else {
                    newMessage = await createMessage(msgvalues).unwrap();
                  }
                  // const newMessage: Message = await createMessage(
                  //   msgvalues
                  // ).unwrap();
                  if (newMessage.id > 0) {
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      creatingMessage: false,
                    }));
                    if (newMessage?.actions?.length > 0) {
                      for (let j = 0; j < newMessage?.actions.length; j++) {
                        const action: Action = newMessage.actions[j];
                        const newAttachments = action?.attachments?.filter(
                          a => a.url
                        );
                        if (newAttachments?.length > 0) {
                          for (let i = 0; i < newAttachments.length; i++) {
                            const attachment: Attachment = newAttachments[i];
                            const file = msgvalues.actions[j].attachments?.find(
                              (a: File) => a.name === attachment.name
                            );
                            if (attachment.url && file.file) {
                              const res = await fetch(attachment.url, {
                                method: 'PUT',
                                body: file.file,
                              });

                              if (res.ok) {
                                setMessageStatus(currentStatus => ({
                                  ...currentStatus,
                                  attachments: {
                                    pending:
                                      currentStatus.attachments.pending - 1,
                                    created:
                                      currentStatus.attachments.created + 1,
                                    failed: currentStatus.attachments.failed,
                                  },
                                }));
                              } else {
                                setMessageStatus(currentStatus => ({
                                  ...currentStatus,
                                  attachments: {
                                    pending:
                                      currentStatus.attachments.pending - 1,
                                    created: currentStatus.attachments.created,
                                    failed:
                                      currentStatus.attachments.failed + 1,
                                  },
                                }));
                              }
                            } else {
                              setMessageStatus(currentStatus => ({
                                ...currentStatus,
                                attachments: {
                                  pending:
                                    currentStatus.attachments.pending - 1,
                                  created: currentStatus.attachments.created,
                                  failed: currentStatus.attachments.failed + 1,
                                },
                              }));
                            }
                          }
                        }
                      }
                    }
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                    }));
                    onSubmit(values);
                    onClose();
                  } else {
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                      creatingMessage: false,
                      creatingMessageError: true,
                    }));
                  }
                } catch (error) {
                  setMessageStatus(currentStatus => ({
                    ...currentStatus,
                    loading: false,
                    creatingMessage: false,
                    creatingMessageError: true,
                  }));
                  console.error('rejected', error);
                }
              }}
              containerGridProps={{ spacing: 2 }}
              form={messageForm}
            />
          </Box>
          {message && message?.created_at && message?.updated_at && (
            <Typography variant="caption">
              <i>
                Message{' '}
                {moment(message.updated_at).isBefore(
                  moment(message.created_at).add(5, 'minutes')
                )
                  ? `sent`
                  : `updated`}{' '}
                {`${moment(message?.updated_at).format(
                  'dddd, MMMM Do YYYY, h:mm a'
                )}`}
              </i>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
